/**
 * formatCmsDate — render a CMS date column for humans, without "Invalid Date".
 *
 * WHY A HELPER
 * ------------
 * Two call sites had byte-identical date formatting, and both were broken in
 * the same way:
 *
 *   - src/components/cards/LectureCard.tsx   (archive + homepage cards)
 *   - src/pages/LectureDetailPage.tsx        (detail metadata row)
 *
 * Both wrapped the parse in `try { … } catch { return raw }`, reasoning that
 * an unparseable string would throw. It doesn't: `new Date("2026年10月13日")`
 * returns an Invalid Date object rather than throwing, and
 * `InvalidDate.prototype.toLocaleDateString()` returns the *string*
 * `"Invalid Date"`. The catch was unreachable, so a lecture ingested with a
 * locale-formatted `event_date` rendered the literal words "Invalid Date" to
 * visitors in production (observed on /zh-Hant/lectures/yada, 2026-10-16).
 *
 * The fix is to test the parsed value, not to rely on control flow. The real
 * defence is upstream — `server/routes/lectures.ts` now validates `event_date`
 * as YYYY-MM-DD on ingest — but rows written before that check (or by hand
 * through SQL) can still carry a local-format string, so the client renders
 * the raw value rather than garbage when the parse fails.
 *
 * The two halves of this repo (src/ and server/) compile under separate
 * tsconfigs, so duplication across the boundary is unavoidable by design;
 * `scripts/lint-form-schemas.ts` is the existing precedent for a test that
 * catches drift between duplicated helpers.
 */

/** BCP-47 locale tags `toLocaleDateString` is happy with. */
type DisplayLocale = string

const OPTIONS: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
}

/**
 * Format `raw` as a localised long date.
 *
 * Returns `raw` unchanged when it is empty or unparseable, so a malformed
 * `event_date` degrades to showing the operator's literal value (which is
 * still informative, e.g. "2026年10月13日") rather than "Invalid Date".
 */
export function formatCmsDate(raw: string | null | undefined, locale: DisplayLocale): string {
  if (!raw || !raw.trim()) return ''
  const parsed = new Date(raw)
  // The check the try/catch was supposed to be doing.
  if (Number.isNaN(parsed.getTime())) return raw
  return parsed.toLocaleDateString(locale, OPTIONS)
}

/**
 * Milliseconds since epoch for `raw`, or `Number.NEGATIVE_INFINITY` when it
 * can't be parsed.
 *
 * Use this instead of comparing date strings lexicographically: `event_date`
 * is documented as YYYY-MM-DD but is `VARCHAR(32)` in the schema and has been
 * observed holding "2026年10月13日". Lexical comparison sorts that string
 * *first* ('2' is 0x32, '-' is 0x2D), silently putting the newest lecture at
 * the top of the archive for the wrong reason.
 */
export function cmsDateSortValue(raw: string | null | undefined): number {
  if (!raw || !raw.trim()) return Number.NEGATIVE_INFINITY
  const t = new Date(raw).getTime()
  return Number.isFinite(t) ? t : Number.NEGATIVE_INFINITY
}